<template>
    <div class="ppv2-space-y-4">
        <span class="poppinsMedium ppv2-text-xs ppv2-text-black ppv2-text-opacity-50 ppv2-block md:ppv2-hidden">
            {{ $t('paymentMethods.dragHint') }}
        </span>
        <div ref="sortableContainer" class="ppv2-payment-sortable ppv2-flex ppv2-flex-col ppv2-space-y-3 ppv2-pt-1">
            <template v-for="paymentMethod in formData.localPaymentMethods">
                <SelectableCard
                    :key="paymentMethod.id"
                    :title="paymentMethod.display_name || paymentMethod.name"
                    :description="paymentMethod.description"
                    :required="paymentMethod.required"
                    :optionalMessage="paymentMethod.optional"
                    :imageSrc="paymentMethod.key === 'stripe' ? '/v2/images/stripe.png' : ''"
                    :imagesSrc="
                        paymentMethod.key === 'stripe'
                            ? [
                                  '/v2/images/cb-icon.svg',
                                  '/v2/images/google-pay-icon.svg',
                                  '/v2/images/apple-pay-icon.svg',
                                  '/v2/images/bancontact-icon.svg',
                              ]
                            : []
                    "
                    :compactImages="paymentMethod.key === 'stripe'"
                    :selected="paymentMethod.selected"
                    :sizeClass="sizeClass"
                    :titleSizeClass="['card', 'stripe'].includes(paymentMethod.key) ? 'ppv2-text-base' : 'ppv2-text-md'"
                    @toggle="toggle(paymentMethod)"
                >
                    <div v-if="paymentMethod.key === 'stripe' && paymentMethod.selected" class="ppv2-mt-4 ppv2-space-y-4">
                        <div class="ppv2-flex ppv2-items-start ppv2-gap-3">
                            <BaseSwitch v-model="formData.shared_payment_enabled" variant="purple" />
                            <div class="ppv2-flex ppv2-flex-col">
                                <span class="poppinsSemiBold ppv2-text-base ppv2-text-black">
                                    {{ $t('configuration.stripeSetup.fields.stripeSharedPayment.label') }}
                                </span>
                                <span class="poppinsRegular ppv2-text-sm ppv2-text-black ppv2-text-opacity-60 ppv2-mt-1">
                                    {{ $t('configuration.stripeSetup.fields.stripeSharedPayment.description') }}
                                </span>
                            </div>
                        </div>

                        <div class="ppv2-flex ppv2-items-start ppv2-gap-3">
                            <BaseSwitch v-model="formData.bancontact_enabled" variant="purple" />
                            <div class="ppv2-flex ppv2-flex-col">
                                <span class="poppinsSemiBold ppv2-text-base ppv2-text-black">
                                    {{ $t('configuration.stripeSetup.fields.stripeBancontact.label') }}
                                </span>
                                <span class="poppinsRegular ppv2-text-sm ppv2-text-black ppv2-text-opacity-60 ppv2-mt-1">
                                    {{ $t('configuration.stripeSetup.fields.stripeBancontact.description') }}
                                </span>
                            </div>
                        </div>

                        <!-- helper link row -->
                        <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                            <svg
                                class="ppv2-w-4 ppv2-h-4 text-purple"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <a
                                :href="$t('configuration.stripeSetup.fields.howToActivateGoogleApplePay.link')"
                                target="_blank"
                                class="poppinsMedium ppv2-text-xs text-purple ppv2-underline ppv2-flex ppv2-items-center ppv2-gap-1"
                            >
                                {{ $t('configuration.stripeSetup.fields.howToActivateGoogleApplePay.label') }}
                            </a>
                        </div>
                    </div>
                </SelectableCard>
            </template>
        </div>

        <InfoMessageBox
            class="ppv2-mt-2"
            :title="$t('messageBox.payment.examples.title')"
            :message="$t('messageBox.payment.examples.text')"
            color="info"
        />
        <InfoMessageBox
            class="ppv2-mt-2"
            :title="$t('messageBox.payment.advice.title')"
            :message="$t('messageBox.payment.advice.text')"
            color="info"
        />
    </div>
</template>

<script>
import SelectableCard from '../../../Shared/SelectableCard.vue'
import InfoMessageBox from '../../../Shared/InfoMessageBox.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import Sortable from 'sortablejs'

export default {
    name: 'PaymentMethods',
    components: {
        InfoMessageBox,
        SelectableCard,
        BaseSwitch,
    },
    data() {
        return {
            customIconStart: `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M2 10H22M4 5H20C21.1046 5 22 5.89543 22 7V17C22 18.1046 21.1046 19 20 19H4C2.89543 19 2 18.1046 2 17V7C2 5.89543 2.89543 5 4 5Z' stroke='#021832' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/>
            </svg>`,
            formData: {
                localPaymentMethods: [],
                shared_payment_enabled: false,
                bancontact_enabled: false,
            },
        }
    },
    computed: {
        establishment() {
            return this.$store.getters['establishment/establishment']
        },
        appSetting() {
            return this.$store.getters['establishment/appSetting']
        },
        sizeClass() {
            return 'ppv2-w-[26px] ppv2-h-[26px]'
        },
    },
    watch: {
        establishment: {
            immediate: true,
            handler() {
                this.syncPaymentMethods()
            },
        },
        'formData.localPaymentMethods': {
            deep: true,
            handler() {
                this.$store.commit('establishment/SET_PAYMENT_METHODS_SELECTED_IDS', this.getSelectedPaymentIds())
            },
        },
        'formData.shared_payment_enabled': {
            handler(newVal) {
                this.$store.commit('establishment/ADD_APP_SETTING', [{ key: 'shared_payment_enabled', value: newVal ? 1 : 0 }])
            },
        },
        'formData.bancontact_enabled': {
            handler(newVal) {
                this.$store.commit('establishment/ADD_APP_SETTING', [{ key: 'bancontact_enabled', value: newVal ? 1 : 0 }])
            },
        },
    },
    created() {
        this.autoFillSharedPayment()
    },
    mounted() {
        this.initSortable()
    },
    methods: {
        autoFillSharedPayment() {
            if (!this.appSetting || !Array.isArray(this.appSetting)) return
            const isOn = (key) => {
                const setting = this.appSetting.find((s) => s.key === key)
                return !!setting && (setting.value == 1 || setting.value === true || setting.value === '1')
            }
            this.formData.shared_payment_enabled = isOn('shared_payment_enabled')
            this.formData.bancontact_enabled = isOn('bancontact_enabled')
        },
        syncPaymentMethods() {
            const storeMethods = this.$store.getters['establishment/paymentMethods']

            const nameToKey = {
                'Remise personnalisée': 'discount',
                cure: 'cure',
                serie: 'series',
                CB: 'card',
                Chèque: 'cheque',
                Espèces: 'cash',
                Virement: 'transfer',
                'Bon cadeau': 'gift_card',
                Stripe: 'stripe',
                Wallet: 'wallet',
                'Billet non daté': 'undated_ticket',
                ANCV: 'ancv',
                'Pass culture': 'culture_pass',
                'Money Banking': 'money_banking',
            }

            const orderedKeys = [
                'card',
                'cash',
                'cheque',
                'transfer',
                'gift_card',
                'cure',
                'series',
                'undated_ticket',
                'wallet',
                'ancv',
                'culture_pass',
                'stripe',
                'money_banking',
            ]
            const requiredMethods = ['CB', 'Espèces', 'Stripe']
            const excludedMethods = ['Remise personnalisée']

            if (!Array.isArray(storeMethods)) {
                this.formData.localPaymentMethods = []
                return
            }

            const previousSelectedIds = this.formData.localPaymentMethods.filter((pm) => pm.selected).map((pm) => pm.id)

            this.formData.localPaymentMethods = storeMethods
                .filter((pm) => !excludedMethods.includes(pm.name))
                .map((pm) => {
                    const key = nameToKey[pm.name]
                    const i18nDetails = key ? this.$t(`paymentMethods.details.${key}`) : {}
                    const establishmentPayment = this.establishment?.payments?.find((p) => p.id === pm.id)

                    let orderValue = pm.display_order || 0
                    if (establishmentPayment && establishmentPayment.pivot && establishmentPayment.pivot.display_order !== null) {
                        orderValue = establishmentPayment.pivot.display_order
                    }

                    return {
                        ...pm,
                        key,
                        description: i18nDetails.description || '',
                        optional: i18nDetails.infoMessage || '',
                        required: requiredMethods.includes(pm.name),
                        selected: previousSelectedIds.includes(pm.id) || !!establishmentPayment || false,
                        display_order: orderValue,
                    }
                })
                .filter((pm) => pm.key)
                .sort((a, b) => a.display_order - b.display_order)
        },

        toggle(paymentMethod) {
            const willBeSelected = !paymentMethod.selected
            this.formData.localPaymentMethods = this.formData.localPaymentMethods.map((pm) =>
                pm.id === paymentMethod.id ? { ...pm, selected: willBeSelected } : pm
            )
            if (paymentMethod.key === 'stripe' && !willBeSelected) {
                this.formData.shared_payment_enabled = false
                this.formData.bancontact_enabled = false
            }
        },

        getSelectedPaymentIds() {
            return this.formData.localPaymentMethods
                .filter((pm) => pm.selected)
                .map((pm) => ({
                    id: pm.id,
                    display_order: pm.display_order,
                }))
        },

        initSortable() {
            const container = this.$refs.sortableContainer
            if (!container) return

            Sortable.create(container, {
                animation: 150,
                delay: 150,
                delayOnTouchOnly: true,
                touchStartThreshold: 10,
                chosenClass: 'ppv2-payment-chosen',
                ghostClass: 'ppv2-payment-ghost',
                onEnd: ({ newIndex, oldIndex }) => {
                    const movedItem = this.formData.localPaymentMethods.splice(oldIndex, 1)[0]
                    this.formData.localPaymentMethods.splice(newIndex, 0, movedItem)

                    // Update order property for all items
                    this.formData.localPaymentMethods.forEach((pm, index) => {
                        pm.display_order = index + 1
                    })

                    this.$store.commit('establishment/SET_PAYMENT_METHODS_SELECTED_IDS', this.getSelectedPaymentIds())
                },
            })
        },
    },
}
</script>

<style>
.ppv2-payment-sortable {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
}

.ppv2-payment-chosen,
.ppv2-payment-ghost {
    border-style: dashed !important;
}

.ppv2-payment-ghost {
    opacity: 0.6;
}
</style>
